/*
 * Copyright (c) Funtip GbR 2017.
 * Simon Oldeboershuis, Perry Prust
 */

import {push} from '@lagunovsky/redux-react-router';
import * as R from 'ramda';
import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Alert, Button, Table} from 'reactstrap';
import {userGroupAdminPath} from '../../links/UserGroupAdminLink';
import {withBorder} from '../../reactutils/withBorder';
import {withNavLocation} from '../../reactutils/withNavLocation';
import {TabbedContent} from '../../reactutils/withTabbedScreen';
import {FuntipTitle} from '../../reactutils/withTitle';
import {GroupInternalHighscore, UserGroup} from '../../services/betting';
import {LoadingIndicatorSpinner2} from '../common/LoadingIndicatorSpinner2';
import {groupInternalHighscoreForwarderPath} from '../GroupInternaltHighscoresForwarder';
import {usePathSegment} from '../SysAdminApp/CacheStatisticsComponent';
import {userGroupChatPath} from '../TeamChat';
import {TournamentSelectionComponent} from '../TournamentSelectionComponent';
import {TournamentRoundSelectionComponent2} from '../TournamentSelectionComponent/TournamentRoundSelectionComponent';
import {UserGroupImage} from '../UserImage';
import {
    gihsColumnLeft,
    gihsColumnRight,
    gihsLoadWithProps,
    gihsSetColumnCount,
    gihsTournamentIdChanged,
} from './actions';
import {LeafGridHeading} from './components/LeafGridHeading';
import {LeafGridRow} from './components/LeafGridRow';
import {NodeGridHeading} from './components/NodeGridHeading';
import {NodeGridRow} from './components/NodeGridRow';
import {
    gihsAvailableUserGroupsSelector,
    gihsColumnCountSelector,
    gihsEntitySelector,
    gihsIsLoadingSelector,
    gihsLeftEnabledSelector,
    gihsRightEnabledSelector,
    gihsStartColumnSelector,
    gihsUserGroupDetailsSelector,
} from './selectors';
import {useWindowWidth} from '@react-hook/window-size';
import {useQuery} from '@tanstack/react-query';
import {getTournamentApi2, getUserGroupAdminApi2} from '../System/systemConfig';
import {tournamentSelectionTournamentIdSelector} from '../TournamentSelectionComponent/selectors';
import {useRootHighscoreLink} from "../../hooks/useRootHighscoreLink";
import {highscoresPath} from "../Highscores";

const GroupInternalHighscores: React.FC<{}> = () => {
    const dispatch = useDispatch();

    const [trId] = usePathSegment(3);
    const [ugId] = usePathSegment(5);

    React.useEffect(() => {
        if (trId && ugId) {
            dispatch(gihsLoadWithProps({trId, ugId}));
        }
    }, [dispatch, ugId, trId]);

    const onSetColumnCount = React.useCallback(
        (count: number) => {
            dispatch(gihsSetColumnCount(count));
        },
        [dispatch],
    );

    const rootHighscoreLink = useRootHighscoreLink();

    // the minimal size of a game column; used for calculating the number of visible columns
    const GAME_COLUMN_WIDTH = 82;
    const HEADER_COLUMN_WIDTH = 110;
    const SUMMARY_COLUMN_WIDTH = 65;

    const windowWidth = useWindowWidth({leading: true});

    React.useEffect(() => {
        const visibleGameCount = Math.floor(
            ((windowWidth > 980 ? 980 : windowWidth) - HEADER_COLUMN_WIDTH - SUMMARY_COLUMN_WIDTH) / GAME_COLUMN_WIDTH,
        );
        console.debug(`viewportWidth: ${windowWidth} => columns: ${visibleGameCount}`);
        onSetColumnCount(visibleGameCount);
    }, [windowWidth, onSetColumnCount]);

    const highscore = useSelector(gihsEntitySelector);

    const columnCount = useSelector(gihsColumnCountSelector);
    const startColumn = useSelector(gihsStartColumnSelector);
    const leftEnabled = useSelector(gihsLeftEnabledSelector);
    const rightEnabled = useSelector(gihsRightEnabledSelector);
    const availableGroups = useSelector(gihsAvailableUserGroupsSelector);
    const ug = useSelector(gihsUserGroupDetailsSelector);

    const tId = useSelector(tournamentSelectionTournamentIdSelector);

    const isLoading = useSelector(gihsIsLoadingSelector);

    const tournamentQuery = useQuery({
        queryKey: ['tournament', tId],
        queryFn: () => getTournamentApi2().getTournamentById({tId: tId!}),
        enabled: !!tId,
    });

    const groupMemberQuery = useQuery({
        queryKey: ['groupMember', ugId],
        queryFn: () => getUserGroupAdminApi2().getGroupMember({ugId}),
        enabled: !!ugId,
    });

    const ugctQuery = useQuery({
        queryKey: ['userGroupClientTournament.subscription', ugId, tId],
        queryFn: () => getUserGroupAdminApi2().getUserGroupTournamentSubscription({ugId, tournamentId: String(tId)}),
        enabled: !!ugId && !!tId,
    });

    const onColumnRight = () => {
        dispatch(gihsColumnRight());
    };
    const onColumnLeft = () => {
        dispatch(gihsColumnLeft());
    };

    function renderHighscoreGrid(gihs: GroupInternalHighscore) {
        if (gihs && gihs.tournamentRound.leaf) {
            return (
                <LoadingIndicatorSpinner2 isLoading={isLoading}>
                    <Table className="table-responsive" striped={false}>
                        <thead>
                        <LeafGridHeading
                            games={gihs.games}
                            columnCount={columnCount}
                            startColumn={startColumn}
                            onColumnLeft={onColumnLeft}
                            onColumnRight={onColumnRight}
                            leftEnabled={leftEnabled}
                            rightEnabled={rightEnabled}
                        />
                        </thead>
                        <tbody>
                        {gihs.tournamentRoundGroupMembers.map((trgm, index) => (
                            <LeafGridRow
                                key={trgm.id}
                                trgm={trgm}
                                bets={gihs.leafResults[index]}
                                user={gihs.users[index]}
                                columnCount={columnCount}
                                startColumn={startColumn}
                            />
                        ))}
                        </tbody>
                    </Table>
                </LoadingIndicatorSpinner2>
            );
        } else if (gihs && !gihs.tournamentRound.leaf) {
            return (
                <Table striped={false} className="sm">
                    <thead>
                    <NodeGridHeading
                        childRounds={gihs.childTournamentRounds}
                        columnCount={columnCount}
                        startColumn={startColumn}
                        onColumnLeft={onColumnLeft}
                        onColumnRight={onColumnRight}
                        leftEnabled={leftEnabled}
                        rightEnabled={rightEnabled}
                    />
                    </thead>
                    <tbody>
                    {gihs.tournamentRoundGroupMembers.map((trgm, index) => (
                        <NodeGridRow
                            key={trgm.id}
                            trgm={trgm}
                            tournamentRoundGroupMembers={gihs.nodeResults[index]}
                            user={gihs.users[index]}
                            columnCount={columnCount}
                            startColumn={startColumn}
                        />
                    ))}
                    </tbody>
                </Table>
            );
        } else {
            return <div className="container">No node and no leaf!</div>;
        }
    }

    const renderAvailableGroup = (ug: UserGroup) => {
        if (highscore) {
            const trId = highscore.tournamentRound.id;
            return (
                <Button
                    key={ug.id}
                    className="me-2 mb-1"
                    color={ug.id === highscore.userGroup.id ? 'warning' : 'secondary'}
                    onClick={() => dispatch(push(groupInternalHighscorePath(String(trId), String(ug.id))))}
                >
                    {ug.name}
                </Button>
            );
        } else {
            return null;
        }
    };

    return (
        <>
            <FuntipTitle
                title="Tippteam"
                subTitle={ug?.name || ''}
                leftComponent={<TournamentSelectionComponent onTournamentChange={gihsTournamentIdChanged}/>}
                rightComponent={<TournamentRoundSelectionSwitch trId={trId} ugId={String(ug?.id)}/>}
            />
            <Button color="secondary"
                    className="me-2 mb-1"
                    onClick={() => dispatch(push(highscoresPath(String(rootHighscoreLink.trId), rootHighscoreLink.absolutPos)))}
            >
                Einzelwertung
            </Button>
            {availableGroups && availableGroups.map((ug) => renderAvailableGroup(ug))}
            <div className="d-flex justify-content-between mt-3 flex-wrap">
                <TabbedContent
                    activeTab={'Wertung'}
                    tabNames={['Wertung', 'Tippteam', 'Chat']}
                    tabLinks={userGroupTabLinks(String(ug?.id))}
                />
                <div>{ug && ug.imageId && <UserGroupImage imageId={ug.imageId} width={200}/>}</div>
            </div>

            {highscore && (
                <>
                    {ugctQuery.data?.subscribed && renderHighscoreGrid(highscore)}
                    {highscore.users?.length === 0 && ugctQuery.data?.subscribed && (
                        <Alert>
                            <h3>Keine Tipps vorhanden</h3>
                            <p>
                                Es wurden keine Tipps für das Tippteam '{highscore.userGroup.name}' abgegeben.
                            </p>
                        </Alert>
                    )}
                    {!ugctQuery.data?.subscribed && (
                        <Alert className="mt-3">
                            <h3>Turnieranmeldung</h3>
                            <p>
                                Das Tippteam hat sich noch nicht für das Turnier '{tournamentQuery.data?.name}'
                                angemeldet.&nbsp;
                                {!groupMemberQuery.data?.admin && (
                                    <span>
                                        Der Administrator des Tippteams kann die Anmeldung vornehmen.
                                    </span>
                                )}
                            </p>
                            <p>
                                <Button
                                    color="success"
                                    onClick={() => dispatch(push(userGroupAdminPath(String(ug?.id))))}
                                >
                                    Zur Anmeldung
                                </Button>
                            </p>
                        </Alert>
                    )}

                    <Button onClick={() => dispatch(push(userGroupAdminPath(highscore.userGroup.id)))}>
                        Zum Tippteam '{highscore.userGroup.name}'
                    </Button>
                </>
            )}
        </>
    );
};

const TournamentRoundSelectionSwitch: React.FC<{ ugId?: string; trId: string }> = (props) => {
    if (props.ugId && props.ugId !== '') {
        return (
            <TournamentRoundSelectionComponent2
                trId={props.trId}
                type="highscores"
                pathByTournamentRound={(trId) => groupInternalHighscorePath(trId, String(props.ugId))}
            />
        );
    } else {
        return null;
    }
};

export const userGroupTabLinks = (ugId: string) => (tabName: string) => {
    if (tabName === 'Tippteam') {
        return userGroupAdminPath(ugId);
    } else if (tabName === 'Wertung') {
        return groupInternalHighscoreForwarderPath(ugId);
    } else {
        return userGroupChatPath(ugId);
    }
};

export const groupInternalHighscorePath = (trId: string, ugId: string) =>
    `/b/group-highscores/${trId}/user-group/${ugId}`;

export const GroupInternalHighscoreScreen = R.compose(
    withBorder(),
    withNavLocation('my-highscore'),
)(GroupInternalHighscores);
